import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchGraphQL } from "../../../Graphql/utils";
import AlertBannerContext from "../../../Contexts/alertBannerContext";
import { ChatBookingConfig } from "../config/chatBookingConfig";
import {
  GetServiceChatBookingById,
  UpdateServiceChatBookinQuery
} from "../../../Graphql/querries/serviceChatBooking";
import {
  GetServiceChatBookingByIdResponse,
  ServiceChatBooking,
  ServiceChatBookingStatus,
  UpdateServiceChatStatusInput,
  UpdateServiceChatStatusResponse
} from "../../../Graphql/types/serviceChatBooking";
import WithAuth from "../../../Hoc/WithAuth";

const ChatBookingDetail: FC = () => {
  const [chatData, setChatData] = useState<ServiceChatBooking | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const { chatBookingId } = useParams();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  // Extract the 'id' from the URL parameters
  const { setAlert } = useContext(AlertBannerContext);
  const { mutate, isPending: isFetching } = useMutation<
    GetServiceChatBookingByIdResponse,
    Error
  >({
    mutationKey: ["getChatBookingById"],
    mutationFn: () =>
      fetchGraphQL(GetServiceChatBookingById, { id: chatBookingId }, setAlert),
    onSuccess: (response) => {
      setChatData(response.getServiceChatBookingById);
    }
  });

  const { mutate: updateChatBooking, isPending } = useMutation<
    UpdateServiceChatStatusResponse,
    Error,
    UpdateServiceChatStatusInput
  >({
    mutationKey: ["updateChatBooking"],
    mutationFn: (input) =>
      fetchGraphQL(UpdateServiceChatBookinQuery, { input }, setAlert),
    onSuccess: (response) => {
      setChatData(response.updateServiceChatBooking);
    }
  });

  useEffect(() => {
    mutate();
  }, []);

  if (isFetching || isPending) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  const updateChatBookingStatus = (
    chatBookStatus: ServiceChatBookingStatus
  ) => {
    const request: UpdateServiceChatStatusInput = {
      id: chatBookingId || "",
      status: chatBookStatus
    };
    updateChatBooking(request);
  };

  return (
    <section style={containerStyle}>
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Chat Booking Detail
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
        <Stack gap={3} flexDirection="row">
          {chatData?.status === ServiceChatBookingStatus.OPEN && (
            <Button
              variant="contained"
              onClick={() => {
                updateChatBookingStatus(ServiceChatBookingStatus.PENDING);
              }}
            >
              Mark as pending
            </Button>
          )}
          {chatData?.status !== ServiceChatBookingStatus.CLOSED && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                updateChatBookingStatus(ServiceChatBookingStatus.CLOSED);
              }}
            >
              Mark as completed
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack
        style={{
          background: "white",
          paddingBottom: "2em",
          gap: "1em",
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          flexDirection: "column"
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "auto",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap"
          }}
        >
          {ChatBookingConfig.map((config) => (
            <Stack
              key={config.key}
              style={{
                border: "solid 0.5px grey",
                padding: "1em",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "center",
                gap: "1em",
                width: "50%"
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {`${config.displayTitle}: `}
              </Typography>
              {config.key === "email" ? (
                <Link href={`mailto:${chatData?.[config.key]}`}>
                  {chatData?.[config.key]}
                </Link>
              ) : (
                <div>
                  {config.key === "phoneNumber" ? (
                    <Link href={`tel:${chatData?.[config.key]}`}>
                      {chatData?.[config.key]}
                    </Link>
                  ) : (
                    <Typography variant="body2">
                      {chatData?.[config.key]}
                    </Typography>
                  )}
                </div>
              )}
            </Stack>
          ))}
        </Box>
      </Stack>
    </section>
  );
};

export default WithAuth(ChatBookingDetail);
